/**
 * Theme Studio "Icons" colour for icon IMAGES (`<img src="x.svg">`), which CSS
 * `color` cannot reach: a CSS `filter` that turns a dark, single-colour icon
 * into the target colour.
 *
 * `brightness(0)` first flattens any dark icon to black, so the result does not
 * depend on which grey the SVG was drawn in. A grey target is exact
 * (`invert(p)` maps black to grey p); any other colour is solved for with SPSA
 * over invert / sepia / saturate / hue-rotate / brightness / contrast — the same
 * search the well-known "hex to CSS filter" generators use, but seeded so one
 * colour always gives the same filter.
 */
type RGB = [number, number, number];

const clamp = (v: number) => Math.max(0, Math.min(255, v));

/** The colour black becomes after `invert sepia saturate hue-rotate brightness contrast`. */
const render = (f: number[]): RGB => {
  let rgb: RGB = [0, 0, 0];
  const multiply = (m: number[]) => {
    const [r, g, b] = rgb;
    rgb = [
      clamp(r * m[0] + g * m[1] + b * m[2]),
      clamp(r * m[3] + g * m[4] + b * m[5]),
      clamp(r * m[6] + g * m[7] + b * m[8]),
    ];
  };
  const linear = (slope: number, intercept: number) => {
    rgb = rgb.map((c) => clamp(c * slope + intercept * 255)) as RGB;
  };

  const inv = f[0] / 100;
  rgb = rgb.map((c) => clamp((inv + (c / 255) * (1 - 2 * inv)) * 255)) as RGB;

  const s = 1 - f[1] / 100;
  multiply([
    0.393 + 0.607 * s, 0.769 - 0.769 * s, 0.189 - 0.189 * s,
    0.349 - 0.349 * s, 0.686 + 0.314 * s, 0.168 - 0.168 * s,
    0.272 - 0.272 * s, 0.534 - 0.534 * s, 0.131 + 0.869 * s,
  ]);

  const sat = f[2] / 100;
  multiply([
    0.213 + 0.787 * sat, 0.715 - 0.715 * sat, 0.072 - 0.072 * sat,
    0.213 - 0.213 * sat, 0.715 + 0.285 * sat, 0.072 - 0.072 * sat,
    0.213 - 0.213 * sat, 0.715 - 0.715 * sat, 0.072 + 0.928 * sat,
  ]);

  const a = (f[3] * Math.PI) / 180;
  const sin = Math.sin(a);
  const cos = Math.cos(a);
  multiply([
    0.213 + cos * 0.787 - sin * 0.213, 0.715 - cos * 0.715 - sin * 0.715, 0.072 - cos * 0.072 + sin * 0.928,
    0.213 - cos * 0.213 + sin * 0.143, 0.715 + cos * 0.285 + sin * 0.14, 0.072 - cos * 0.072 - sin * 0.283,
    0.213 - cos * 0.213 - sin * 0.787, 0.715 - cos * 0.715 + sin * 0.715, 0.072 + cos * 0.928 + sin * 0.072,
  ]);

  linear(f[4] / 100, 0);
  const c = f[5] / 100;
  linear(c, -(0.5 * c) + 0.5);
  return rgb;
};

const hsl = ([r, g, b]: RGB): RGB => {
  const [rr, gg, bb] = [r / 255, g / 255, b / 255];
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === rr ? (gg - bb) / d + (gg < bb ? 6 : 0) : max === gg ? (bb - rr) / d + 2 : (rr - gg) / d + 4;
  h /= 6;
  return [h * 100, s * 100, l * 100];
};

const loss = (f: number[], target: RGB, targetHsl: RGB): number => {
  const got = render(f);
  const gotHsl = hsl(got);
  return (
    Math.abs(got[0] - target[0]) +
    Math.abs(got[1] - target[1]) +
    Math.abs(got[2] - target[2]) +
    Math.abs(gotHsl[0] - targetHsl[0]) +
    Math.abs(gotHsl[1] - targetHsl[1]) +
    Math.abs(gotHsl[2] - targetHsl[2])
  );
};

/** mulberry32 — a fixed seed keeps the filter stable for a colour. */
const seeded = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const fix = (v: number, i: number) => {
  const max = i === 2 ? 7500 : i === 4 || i === 5 ? 200 : 100;
  if (i === 3) return v > 360 ? v % 360 : v < 0 ? (v % 360) + 360 : v;
  return v < 0 ? 0 : v > max ? max : v;
};

const spsa = (A: number, a: number[], c: number, start: number[], iters: number, target: RGB, targetHsl: RGB, rand: () => number) => {
  const alpha = 1;
  const gamma = 1 / 6;
  let best = start.slice();
  let bestLoss = Infinity;
  const values = start.slice();
  for (let k = 0; k < iters; k++) {
    const ck = c / Math.pow(k + 1, gamma);
    const deltas = values.map(() => (rand() > 0.5 ? 1 : -1));
    const high = values.map((v, i) => v + ck * deltas[i]);
    const low = values.map((v, i) => v - ck * deltas[i]);
    const diff = loss(high, target, targetHsl) - loss(low, target, targetHsl);
    for (let i = 0; i < values.length; i++) {
      const g = (diff / (2 * ck)) * deltas[i];
      const ak = a[i] / Math.pow(A + k + 1, alpha);
      values[i] = fix(values[i] - ak * g, i);
    }
    const l = loss(values, target, targetHsl);
    if (l < bestLoss) {
      best = values.slice();
      bestLoss = l;
    }
  }
  return { values: best, loss: bestLoss };
};

const solve = (target: RGB): number[] => {
  const targetHsl = hsl(target);
  const rand = seeded(target[0] * 65536 + target[1] * 256 + target[2] + 1);
  let wide = { values: [] as number[], loss: Infinity };
  for (let i = 0; wide.loss > 25 && i < 6; i++) {
    const r = spsa(5, [60, 180, 18000, 600, 1.2, 1.2], 15, [50, 20, 3750, 50, 100, 100], 1000, target, targetHsl, rand);
    if (r.loss < wide.loss) wide = r;
  }
  const A = wide.loss;
  const narrow = spsa(A, [0.25 * (A + 1), 0.25 * (A + 1), A + 1, 0.25 * (A + 1), 0.2 * (A + 1), 0.2 * (A + 1)], 2, wide.values, 500, target, targetHsl, rand);
  return narrow.loss < wide.loss ? narrow.values : wide.values;
};

const cache = new Map<string, string>();

/** CSS `filter` that paints a dark single-colour icon in `rgb`. */
export const iconFilter = (rgb: RGB): string => {
  const key = rgb.join(",");
  const hit = cache.get(key);
  if (hit) return hit;
  const [r, g, b] = rgb;
  let filter: string;
  if (Math.max(r, g, b) - Math.min(r, g, b) <= 4) {
    filter = `brightness(0) invert(${(((r + g + b) / 3 / 255) * 100).toFixed(1)}%)`;
  } else {
    const f = solve(rgb);
    filter =
      `brightness(0) saturate(100%) invert(${Math.round(f[0])}%) sepia(${Math.round(f[1])}%) ` +
      `saturate(${Math.round(f[2])}%) hue-rotate(${Math.round(f[3])}deg) brightness(${Math.round(f[4])}%) contrast(${Math.round(f[5])}%)`;
  }
  cache.set(key, filter);
  return filter;
};
